import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Binoculars } from "@phosphor-icons/react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TOKEN_KEY = "np:reactor-token";

function reactorToken() {
  try {
    let t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      t = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
  } catch {
    return null;
  }
}

/** One-tap community confirmation — "I saw this too". No account required:
 * guests are counted via a localStorage reactor token, members by account.
 * Toggle on/off, optimistic UI. */
export default function SawThisToo({ obsId, testid = "saw-this-too" }) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    http.get(`/nature/observations/${obsId}/saw-this-too`, { params: { token: reactorToken() } })
      .then(({ data }) => { if (alive) { setCount(data.count || 0); setConfirmed(!!data.confirmed); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [obsId, user?.firebase_uid]);

  const toggle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    const wasConfirmed = confirmed;
    // optimistic
    setConfirmed(!wasConfirmed);
    setCount((n) => Math.max(0, n + (wasConfirmed ? -1 : 1)));
    try {
      const { data } = await http.post(`/nature/observations/${obsId}/saw-this-too`, { anon_token: reactorToken() });
      setCount(data.count || 0);
      setConfirmed(!!data.confirmed);
    } catch {
      setConfirmed(wasConfirmed);
      setCount((n) => Math.max(0, n + (wasConfirmed ? 1 : -1)));
      toast.error("Couldn't save that — try again");
    } finally {
      setBusy(false);
    }
  }, [busy, confirmed, obsId]);

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={confirmed}
      data-testid={`${testid}-button`}
      title="Confirm you've seen this too — one tap, no account needed"
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase transition-colors border ${
        confirmed
          ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
          : "border-[#1B4332]/40 bg-[#1B4332]/8 text-[#1B4332] hover:bg-[#1B4332] hover:text-[#F5EBDC]"
      } disabled:opacity-60`}
    >
      <Binoculars size={15} weight={confirmed ? "fill" : "bold"} />
      I saw this too
      <span data-testid={`${testid}-count`} className={`rounded-full px-1.5 py-0.5 text-[10px] ${confirmed ? "bg-[#F5EBDC]/20" : "bg-[#1B4332]/10"}`}>
        {count}
      </span>
    </button>
  );
}
